import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/AuthContext";
import { Loader2, LogIn, UserPlus } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        if (!name.trim()) { setError("Name is required"); setLoading(false); return; }
        await register(email, password, name);
      }
      navigate("/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="heading text-3xl font-bold tracking-tight uppercase">Raid Tracker</h1>
          <p className="text-muted-foreground text-sm mt-2">
            {mode === "login" ? "Sign in to manage raids" : "Register as an officer"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1">Name</label>
              <Input
                data-testid="login-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your character name"
                className="bg-card/50"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1">Email</label>
            <Input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="bg-card/50"
              required
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1">Password</label>
            <Input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="bg-card/50"
              required
            />
          </div>

          {error && (
            <p className="text-sm text-destructive" data-testid="login-error">{error}</p>
          )}

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full gap-2 bg-foreground text-background hover:bg-foreground/90 rounded-sm"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> :
              mode === "login" ? <LogIn className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            {mode === "login" ? "Sign In" : "Register"}
          </Button>
        </form>

        <div className="text-center mt-6">
          {mode === "login" ? (
            <p className="text-sm text-muted-foreground">
              Officer?{" "}
              <button data-testid="switch-to-register" onClick={() => { setMode("register"); setError(""); }}
                className="text-foreground hover:underline">Register here</button>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Already registered?{" "}
              <button data-testid="switch-to-login" onClick={() => { setMode("login"); setError(""); }}
                className="text-foreground hover:underline">Sign in</button>
            </p>
          )}
        </div>

        <div className="text-center mt-4">
          <button onClick={() => navigate("/")}
            className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors">
            Continue as visitor (read-only)
          </button>
        </div>
      </div>
    </div>
  );
}
